import { get, post, patch, del, put } from 'modules/api';
import Utils from 'modules/utils';

const { apiLinkTo } = Utils;

/**
 * Get history of distribution between 2 dates.
 *
 * @param {Number} farmId
 * @param {Number} page
 * @returns
 */
export const getProducerHistory = (farmId, start, end, perPage = 10) =>
    get(`farms/${farmId}/distributions/archive`, { start, end, per_page: perPage });

/**
 * Download .zip of invoices between start date and end date.
 *
 * @param {Number} farmId
 * @param {Number} _.month
 * @param {Number} _.year
 * @param {Object} params
 * @returns
 */
export const downloadInvoices = (farmId, { month, year }, params) =>
    apiLinkTo(`farms/${farmId}/invoices_${month}_${year}.zip{?q*}`, {
        hash: {
            q: params,
        },
    });

export const getProducerYearMonthArchiveHistory = farmId =>
    get(`farms/${farmId}/distributions/finalized_years_and_month`);

export const createFarm = () => post('farms/');

export const getInvitations = farmId => get(`farms/${farmId}/invitations/pending`);

export const getAvailabilityDates = ({ farmId }) => get(`farms/${farmId}/financialDocuments`);

export const patchPendingAction = farmId =>
    patch(`farms/${farmId}/pending`, {
        action: 'pending_action',
    });

export const getGuestsStats = farmId => {
    return get(`farms/${farmId}/sales/guest/stats`);
};

export const getLocalStats = farmId => {
    return get(`farms/${farmId}/sales/local/stats`);
};

export const getFarmWithHives = forFarm => get(`farms/${forFarm}/with_hives`);

export const getFarmWithHive = (forFarm, fromAssembly) =>
    get(`farms/${forFarm}/hives/${fromAssembly}`);

export const patchFarm = (farmId, data) => {
    return patch(`farms/${farmId}`, data);
};

export const getSourceFarms = farmId => get(`farms/${farmId}/source-farms`);

export const addSourceFarm = (farmId, newPartner) =>
    post(`farms/${farmId}/source-farms`, newPartner);

export const editSourceFarm = (farmId, sourceFarmId, data) =>
    patch(`farms/${farmId}/source-farms/${sourceFarmId}`, data);

export const deleteSourceFarm = (farmId, sourceFarmId) =>
    del(`farms/${farmId}/source-farms/${sourceFarmId}`);

export const setDistributionMinimumOrder = (farmId, distributionId, amount, currency) =>
    put(`farms/${farmId}/distributions/${distributionId}`, {
        orderMinimum: {
            amount,
            currency,
        },
    });

export const setDistributionMinimumOrderForAssembly = (farmId, hiveId, amount, currency) =>
    put(`farms/${farmId}/hives/${hiveId}`, {
        orderMinimum: {
            amount,
            currency,
        },
    });

export const requestPending = farmId =>
    patch(`farms/${farmId}/pending`, {
        action: 'pending_action',
    });

export const fetchFarm = farmId => get(`farms/${farmId}`);

export const setCompanyId = ({ farmId, companyId }) =>
    put(`farms/${farmId}/companies/${companyId}`);

export const getVATRates = farmId => get(`farms/${farmId}/vat_rates`);

export const getHivesByFarm = farmId => get(`farms/${farmId}/hives`);

export const patchDeliveryMeanByFarmAndAssembly = (farmId, assemblyId, { deliveryMean }) =>
    patch(`farms/${farmId}/hives/${assemblyId}/scopes`, {
        deliveryMean,
    });
export const putMinimumOrderOfAnAssembly = (farmId, assemblyId, { orderMinimum }) =>
    put(`farms/${farmId}/hives/${assemblyId}`, {
        orderMinimum,
    });

export const deleteCollaborationWithAssembly = (farmId, assemblyId) =>
    del(`farms/${farmId}/hives/${assemblyId}`);

export const getProductsFeedbacks = (farmId, period) =>
    get(`farms/${farmId}/products/feedbacks?from=${encodeURIComponent(`-${period}`)}`);

export const getFinancialDocuments = farmId => get(`farms/${farmId}/financialDocuments`);

export const postRemoveProduct = (farmId, payload) =>
    post(`farms/${farmId}/removeProducts`, payload);

export const postCancelProductsRemoval = (farmUuid, distributionUuid) =>
    post(`farms/${farmUuid}/cancelProductsRemoval`, {
        distributions: [distributionUuid],
    });

export const postMultiplePdfEmail = ({ farmId, distributionsIds }) =>
    post(`farms/${farmId}/documents/generate/orders?${distributionsIds}`);

export const postFarmAvailability = (farmId, payload) => post(`farms/${farmId}/calendar`, payload);

export const getDistributionsForFarm = farmId => get(`farms/${farmId}/distributions`);

export const getOpenedDistributionsFollowUpForFarm = farmId =>
    get(`farms/${farmId}/followup/opened_distributions`);

export const getProducerDistributionHistory = (farmId, page) =>
    get(`farms/${farmId}/distributions/archive`, { page });

export const getAssembliesNearFarm = (farmId, limit, offset, name, day) =>
    get(`farms/${farmId}/search_hives`, {
        limit,
        offset,
        name,
        day,
    });

export const fetchPlanningForProducer = (farmId, startDate, endDate) =>
    get(`farms/${farmId}/planning`, {
        startDate,
        endDate,
    });

export const fetchUnavailabilitiesProducer = ({ farmId }) =>
    get(`farms/${farmId}/unavailabilities`);

export const sendUnavailabilitiesProducer = ({ farmId, startDate, endDate }) =>
    post(`farms/${farmId}/unavailabilities`, {
        startDate,
        endDate,
    });

export const deleteUnavailabilitiesProducer = ({ farmId, dateId }) =>
    del(`farms/${farmId}/unavailabilities/${dateId}`);

export const getProducerStats = farmId => get(`farms/${farmId}/dashboard`);

export const fetchNewsForFarm = farmUuid => get(`farms/${farmUuid}/newsfeed`);

export const cancelRepresentationAsCarrier = (
    carrierProducerId,
    distributionId,
    sourceProducerId
) =>
    del(
        `farms/${carrierProducerId}/distributions/${distributionId}/representations/source/${sourceProducerId}`
    );

export const cancelDeliveryDelegationAsProvider = (
    providerProducerId,
    distributionId,
    sourceProducerId
) =>
    del(
        `farms/${providerProducerId}/distributions/${distributionId}/delivery/source/${sourceProducerId}`
    );

export const getFarmProducts = (farmId, params) =>
    get(`farms/${farmId}/products`, { scope: 'local', ...params });

export const importProductsInInterregionalCatalog = (farmId, productIds) =>
    post(`farms/${farmId}/create_interregional_products`, { products: productIds });

export const putHasPublicProfile = (farmId, hasPublicProfile) =>
    put(`farms/${farmId}/public-profile`, {
        hasPublicProfile,
    });
